import { Image } from 'expo-image';
import { useState } from 'react';
import { StyleSheet } from 'react-native';

import {
  type ImageAssetWithAltText,
  RadioGroup,
  Skeleton,
  Text,
  YStack,
  tokens,
  usePressableState,
  useRadioItem,
} from '@fhs/ui';

import type { PickerAspectOptionSelectorProps } from './types';

export function HorizontalPickerAspectOptionSelector(props: PickerAspectOptionSelectorProps) {
  const [finishedImageKeys, setFinishedImageKeys] = useState<Set<string>>(() => new Set());
  const imageKeys = props.options.flatMap(option =>
    option.image?.asset?.uri ? [`${option.value}:${option.image.asset.uri}`] : []
  );
  const areImagesReady =
    !props.synchronizeImageLoading || imageKeys.every(key => finishedImageKeys.has(key));

  const handleImageLoadEnd = (key: string) => {
    setFinishedImageKeys(current => {
      if (current.has(key)) {
        return current;
      }

      return new Set(current).add(key);
    });
  };

  return (
    <RadioGroup style={styles.radioGroup} value={props.value} onChange={props.onChange}>
      {props.options.map(option => (
        <RadioGroup.Item
          key={option.value}
          value={option.value}
          label={[option.title, option.subtitle].filter(Boolean).join(', ')}
          disabled={option.disabled}
        >
          <PickerAspectCard
            title={option.title}
            subtitle={option.subtitle}
            image={option.image}
            imageLoadKey={
              option.image?.asset?.uri ? `${option.value}:${option.image.asset.uri}` : undefined
            }
            imageVisible={areImagesReady}
            onImageLoadEnd={handleImageLoadEnd}
            isPriceLoading={option.isPriceLoading}
            disableImageCache={option.disableImageCache}
          />
        </RadioGroup.Item>
      ))}
    </RadioGroup>
  );
}

const styles = StyleSheet.create({
  radioGroup: {
    flexDirection: 'row',
    gap: 16,
  },
});

type PickerAspectCardProps = {
  title: string;
  subtitle?: string;
  image?: ImageAssetWithAltText;
  imageLoadKey?: string;
  imageVisible: boolean;
  onImageLoadEnd: (url: string) => void;
  isPriceLoading?: boolean;
  disableImageCache?: boolean;
};

function PickerAspectCard({
  title,
  subtitle,
  image,
  imageLoadKey,
  imageVisible,
  onImageLoadEnd,
  isPriceLoading,
  disableImageCache,
}: PickerAspectCardProps) {
  const radioItem = useRadioItem();
  const cmsImageAlt = image?.altText?.trim();

  const imageUrl = image?.asset?.uri && new URL(image.asset.uri);
  if (imageUrl && radioItem.disabled) {
    imageUrl.searchParams.set('sat', '-100');
  }

  return (
    <RadioGroup.Pressable
      pressedStyle={cardStyles.selectedContainer}
      style={[
        cardStyles.container,
        !radioItem.disabled && radioItem.selected && cardStyles.selectedContainer,
      ]}
      borderRadius={8}
      testID="product-list-item-button"
    >
      {imageUrl ? (
        <Image
          style={[
            cardStyles.image,
            !radioItem.selected && cardStyles.unselectedImage,
            radioItem.disabled && cardStyles.unavailableImage,
            !imageVisible && cardStyles.imagePending,
          ]}
          cachePolicy={disableImageCache ? 'none' : 'memory-disk'}
          contentFit="contain"
          accessibilityLabel={cmsImageAlt || title}
          alt={cmsImageAlt || title}
          source={imageUrl.toString()}
          placeholder={image?.asset?.blurHash ? { blurhash: image.asset.blurHash } : undefined}
          onLoadEnd={() => imageLoadKey && onImageLoadEnd(imageLoadKey)}
        />
      ) : null}
      <PickerAspectCardTextContainer
        selected={radioItem.selected}
        title={title}
        subtitle={radioItem.disabled ? 'Unavailable' : subtitle}
        disabled={radioItem.disabled}
        isPriceLoading={isPriceLoading}
      />
    </RadioGroup.Pressable>
  );
}

function PickerAspectCardTextContainer({
  selected,
  title,
  subtitle,
  disabled,
  isPriceLoading,
}: {
  selected: boolean;
  title: string;
  subtitle?: string;
  disabled?: boolean;
  isPriceLoading?: boolean;
}) {
  const pressableState = usePressableState();
  const shouldTextBeMuted =
    (!pressableState.pressed && !selected) || pressableState.disabled || disabled;

  return (
    <YStack style={cardStyles.textContainer}>
      <Text.Ui
        size="md"
        weight="semibold"
        style={[cardStyles.text, shouldTextBeMuted && cardStyles.textMuted]}
      >
        {title}
      </Text.Ui>
      {isPriceLoading ? (
        <Skeleton
          style={[cardStyles.subtitleSkeleton, shouldTextBeMuted && cardStyles.textMutedSkeleton]}
        />
      ) : subtitle ? (
        <Text.Ui
          size="sm"
          numberOfLines={1}
          style={[cardStyles.text, shouldTextBeMuted && cardStyles.textMuted]}
        >
          {subtitle}
        </Text.Ui>
      ) : null}
    </YStack>
  );
}

const cardStyles = StyleSheet.create({
  container: {
    flexBasis: 100,
    flexShrink: 0,
    flexGrow: 1,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity10,
    paddingTop: 20,
    paddingRight: 12,
    paddingBottom: 16,
    paddingLeft: 12,
    gap: 8,
  },
  selectedContainer: {
    borderColor: tokens.colors.$houseYellow,
  },
  image: {
    height: 40,
    width: '100%',
  },
  imagePending: {
    opacity: 0,
  },
  unselectedImage: {
    opacity: 0.5,
  },
  unavailableImage: {
    opacity: 0.3,
  },
  textContainer: {
    alignItems: 'center',
    maxWidth: '100%',
    gap: 2,
  },
  text: {
    textAlign: 'center',
  },
  textMuted: {
    color: tokens.colors.$disabledText,
  },
  subtitleSkeleton: {
    height: 14,
    width: 44,
    borderRadius: 10,
    backgroundColor: tokens.colors.$houseLight,
  },
  textMutedSkeleton: {
    opacity: 0.6,
  },
});
